import { Head, Link } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatUSDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface TaskItem {
  id: number
  task_group: "case" | "customer"
  due_date: string | null
  urgent: boolean
  category: string | null
  name: string
  name2: string | null
  title: string | null
  notes: string | null
  case_id: number | null
  customer_id: number | null
  event_id: number | null
  assigned_to: string | null
}

interface Props {
  employee: { id: number; name: string }
  tasks: TaskItem[]
  show_assigned_to: boolean
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Home", href: "/dashboard" },
  { title: "Pending Tasks", href: "/team/tasks" },
]

const PER_PAGE = 25

function taskHref(task: TaskItem): string {
  if (task.task_group === "case" && task.case_id) {
    return `/cases/${task.case_id}/logs/${task.id}`
  }
  if (task.task_group === "customer" && task.customer_id) {
    return `/customers/${task.customer_id}/logs/${task.id}`
  }
  return "#"
}

function isOverdue(dateStr: string | null): boolean {
  if (!dateStr) return false
  const d = new Date(dateStr)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return d < today
}

export default function TeamTasks({ tasks, show_assigned_to }: Props) {
  const showAssignedTo = show_assigned_to
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(tasks.length / PER_PAGE)
  const start = (page - 1) * PER_PAGE
  const end = Math.min(start + PER_PAGE, tasks.length)
  const displayed = tasks.slice(start, end)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Pending Tasks" />

      <div className="mx-auto max-w-5xl px-6 py-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-hpi-dark-blue text-xl font-semibold">
            Pending Tasks
          </h1>
          {tasks.length > PER_PAGE && (
            <div className="text-hpi-med-gray flex items-center gap-2 text-sm">
              <span>
                {start + 1}–{end} of {tasks.length}
              </span>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded border px-2 py-0.5 disabled:opacity-40"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded border px-2 py-0.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {tasks.length === 0 ? (
          <div className="text-hpi-med-gray py-12 text-center">
            No pending tasks
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-hpi-light-gray border-b text-left text-xs font-medium text-gray-500">
                <th className="px-3 py-2">Due Date</th>
                <th className="px-3 py-2">Name / Customer</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Title / Notes</th>
                {showAssignedTo && <th className="px-3 py-2">Assigned To</th>}
                <th className="w-8 px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((task) => {
                const overdue = isOverdue(task.due_date)
                return (
                  <tr
                    key={`${task.task_group}-${task.id}`}
                    className={`border-hpi-light-gray/50 border-b text-sm hover:bg-gray-50 ${task.urgent ? "bg-red-50" : ""}`}
                  >
                    <td
                      className={`px-3 py-2 whitespace-nowrap ${overdue ? "font-bold" : ""}`}
                    >
                      <Link
                        href={taskHref(task)}
                        className="text-hpi-active-blue hover:underline"
                      >
                        {task.due_date ? formatUSDate(task.due_date) : "—"}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-hpi-dark-gray font-medium">
                        {task.name}
                      </div>
                      {task.name2 && (
                        <div className="text-hpi-med-gray text-xs">
                          {task.name2}
                        </div>
                      )}
                    </td>
                    <td className="text-hpi-dark-gray px-3 py-2">
                      {task.category || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-hpi-dark-gray">
                        {task.title || "—"}
                      </div>
                      {task.notes && (
                        <div className="text-hpi-med-gray max-w-xs truncate text-xs">
                          {task.notes}
                        </div>
                      )}
                    </td>
                    {showAssignedTo && (
                      <td className="text-hpi-dark-gray px-3 py-2 text-sm">
                        {task.assigned_to || "—"}
                      </td>
                    )}
                    <td className="px-3 py-2 text-center">
                      {task.urgent && (
                        <span
                          className="text-xs font-bold text-red-600"
                          title="Urgent"
                        >
                          !
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppLayout>
  )
}
